import './Unlimited.css';
import { useState, useEffect } from 'react';
import GameStats from './GameStats.js';
import {
  PRICE_EVENT,
  PURCHASE_FAILED_EVENT,
  isBridgeAvailable,
  requestPrice,
  requestPurchase,
  requestRestore,
} from './bridge.js';

// Only a fallback for the web build. In the app, StoreKit gives us the real
// localized string, since each storefront has its own currency and tier price.
const FALLBACK_PRICE = '$3.99';

// StoreKit only exists on the native side, so the web build can't take money.
const isAppBuild = process.env.REACT_APP_EMBEDDED === 'true';

// A small number is a weak argument - only surface the count once it is one
const STAT_THRESHOLD = 100;

const HistoryIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const UndoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
  </svg>
);

const NoAdsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
  </svg>
);

// Framed as what you gain, not what goes away - someone opening this may not
// have hit an ad yet, and "never see an ad again" would be how they find out.
const PERKS = [
  {
    key: 'history',
    icon: <HistoryIcon />,
    title: 'Replay old games',
    detail: 'Go back to any board in your history.',
  },
  {
    key: 'undo',
    icon: <UndoIcon />,
    title: 'Unlimited undos',
    detail: 'Step back as far as you like and keep exploring.',
  },
  {
    key: 'ads',
    icon: <NoAdsIcon />,
    title: 'No ads forever',
    detail: 'Bought once, gone for good.',
  },
];

// When something specific sent the player here, that perk answers what they
// just hit - in their own numbers - instead of repeating a pitch they walked
// past. Keyed by context.from; anything else falls back to the generic detail.
const LIVE_DETAILS = {
  undo: ({ undoLimit }) => undoLimit && `0 of ${undoLimit} left on this board.`,
  history: (_ctx, boards) =>
    boards > 0 && `${boards} ${boards === 1 ? 'board' : 'boards'} waiting to be replayed.`,
  ads: () => 'Gone forever, starting now.',
};

function Unlimited({ onClose, context = null }) {
  const [status, setStatus] = useState(null);
  // Which action is in flight - null, 'purchase' or 'restore'. A plain boolean
  // put the spinner on the buy button while a restore was running, which on a
  // payment screen reads as "we are charging you" when nothing of the kind is
  // happening.
  const [pending, setPending] = useState(null);
  const [price, setPrice] = useState(isAppBuild ? null : FALLBACK_PRICE);
  const boardsPlayed = GameStats.getDistinctGames().length;

  useEffect(() => {
    const onPrice = (e) => setPrice(e.detail);
    // A cancel arrives with no reason - just release the button, say nothing
    const onFailed = (e) => {
      setPending(null);
      setStatus(e.detail || null);
    };
    window.addEventListener(PRICE_EVENT, onPrice);
    window.addEventListener(PURCHASE_FAILED_EVENT, onFailed);
    requestPrice();
    return () => {
      window.removeEventListener(PRICE_EVENT, onPrice);
      window.removeEventListener(PURCHASE_FAILED_EVENT, onFailed);
    };
  }, []);

  // Native owns the StoreKit transaction. Success comes back as setUnlimited,
  // which closes this sheet from App; failure comes back as purchaseFailed.
  const start = (action, send) => {
    if (!isBridgeAvailable()) {
      setStatus('Purchases are only available in the Total Ten app.');
      return;
    }
    setStatus(null);
    setPending(action);
    send();
  };

  return (
    <div className="hud-overlay" onClick={onClose}>
      <div className="unlimited-card hud" onClick={(e) => e.stopPropagation()}>
        <button className="hud-close" onClick={onClose} aria-label="Close">&#x2715;</button>

        <div className="unlimited-head">
          <span className="unlimited-badge">Premium Upgrade</span>
          <h2>Total Ten Unlimited</h2>
          {/* <p className="unlimited-sub">One payment. Everything opens up.</p> */}
        </div>

        <ul className="unlimited-perks">
          {PERKS.map(({ key, icon, title, detail }) => {
            // Highlight on `from` alone, so a perk without live copy - or one
            // whose copy needs numbers we do not have yet - still lights up
            const asked = key === context?.from;
            const text = (asked && LIVE_DETAILS[key]?.(context, boardsPlayed)) || detail;
            return (
              <li key={key} className={asked ? 'unlimited-perk-live' : undefined}>
                <span className="unlimited-perk-icon">{icon}</span>
                <span className="unlimited-perk-body">
                  <span className="unlimited-perk-title">{title}</span>
                  {text && (
                    <span className={'unlimited-perk-detail' + (asked ? ' unlimited-perk-detail-live' : '')}>
                      {text}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>

        {boardsPlayed > STAT_THRESHOLD && (
          <p className="unlimited-stat">
            You have played {boardsPlayed} boards.
          </p>
        )}

        {/* The label is held in place behind the spinner rather than replaced,
            so the button cannot change size at the moment of paying - a
            control that resizes under the thumb reads as something going
            wrong. aria-busy says the same thing the spinner does. */}
        <button
          className="unlimited-buy"
          onClick={() => start('purchase', requestPurchase)}
          disabled={pending !== null}
          aria-busy={pending === 'purchase'}
        >
          <span className={pending === 'purchase' ? 'unlimited-label-hidden' : undefined}>
            {price ? `Unlock for ${price}` : 'Unlock'}
          </span>
          {pending === 'purchase' && <span className="unlimited-spinner" aria-hidden="true" />}
        </button>

        <button
          className="unlimited-restore"
          onClick={() => start('restore', requestRestore)}
          disabled={pending !== null}
          aria-busy={pending === 'restore'}
        >
          <span className={pending === 'restore' ? 'unlimited-label-hidden' : undefined}>
            Restore purchase
          </span>
          {pending === 'restore' && <span className="unlimited-spinner unlimited-spinner-quiet" aria-hidden="true" />}
        </button>

        {status && <p className="unlimited-status">{status}</p>}
        {!isAppBuild && !status && (
          <p className="unlimited-note">One-time purchase · not a subscription</p>
        )}

        {/* <p className="unlimited-legal">One-time purchase · not a subscription</p> */}
      </div>
    </div>
  );
}

export default Unlimited;
